import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type Category = {
  id: string;
  title: string;
  orderIndex: number;
  isActive: boolean;
  updatedAt: string;
};

const AnlasmaliCategoriesYonetim: React.FC = () => {

  const [items, setItems] = React.useState<Category[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  // Create form state
  const [newId, setNewId] = React.useState('');
  const [newTitle, setNewTitle] = React.useState('');
  const [newOrder, setNewOrder] = React.useState<number>(0);
  const [newActive, setNewActive] = React.useState(true);
  const [creating, setCreating] = React.useState(false);

  // Edit state
  const [editingId, setEditingId] = React.useState<string>('');
  const [editTitle, setEditTitle] = React.useState('');
  const [editOrder, setEditOrder] = React.useState<number>(0);
  const [editActive, setEditActive] = React.useState<boolean>(true);
  const [saving, setSaving] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<string>('');
  const [confirmId, setConfirmId] = React.useState<string>('');
  const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false);

  const fetchList = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl('/api/anlasmali-categories/admin'));
      if (!res.ok) throw new Error('Liste alınamadı');
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { fetchList(); }, [fetchList]);

  const resetCreate = () => {
    setNewId('');
    setNewTitle('');
    setNewOrder(0);
    setNewActive(true);
  };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newId.trim() || !newTitle.trim()) {
      setError('Lütfen ID ve Başlık girin');
      return;
    }
    try {
      setCreating(true);
      setError('');
      const res = await fetch(toUrl('/api/anlasmali-categories/admin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: newId.trim(), title: newTitle.trim(), orderIndex: Number(newOrder) || 0, isActive: !!newActive }),
      });
      if (res.status === 409) throw new Error('Aynı ID ile kayıt mevcut');
      if (!res.ok) throw new Error('Oluşturma başarısız');
      resetCreate();
      await fetchList();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (c: Category) => {
    setEditingId(c.id);
    setEditTitle(c.title);
    setEditOrder(c.orderIndex);
    setEditActive(c.isActive);
  };
  const cancelEdit = () => {
    setEditingId('');
    setEditTitle('');
    setEditOrder(0);
    setEditActive(true);
  };

  const onSave = async (id: string) => {
    if (!editTitle.trim()) { setError('Lütfen Başlık girin'); return; }
    try {
      setSaving(true);
      setError('');
      const res = await fetch(toUrl(`/api/anlasmali-categories/admin/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editTitle.trim(), orderIndex: Number(editOrder) || 0, isActive: !!editActive }),
      });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEdit();
      await fetchList();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = (id: string) => setConfirmId(id);
  const confirmDelete = async () => {
    if (!confirmId) return;
    try {
      setConfirmLoading(true);
      setDeletingId(confirmId);
      const res = await fetch(toUrl(`/api/anlasmali-categories/admin/${confirmId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Silme başarısız');
      setConfirmId('');
      await fetchList();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setConfirmLoading(false);
      setDeletingId('');
    }
  };

  return (
    <div className="admin-page container-fluid">
      <div className="d-flex align-items-baseline justify-content-between mb-3">
        <h2 className="mb-0">Anlaşmalı Kategoriler</h2>
        <small className="text-muted">Toplam: {items.length}</small>
      </div>

      {/* Create form */}
      <div className="card mb-3">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h3 className="h5 mb-0">Yeni Kategori Ekle</h3>
          <small className="text-muted">ID benzersiz olmalıdır</small>
        </div>
        <div className="card-body">
          <form onSubmit={onCreate} className="row g-3 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label">ID</label>
              <input type="text" value={newId} onChange={(e) => setNewId(e.target.value)} className="form-control" required />
            </div>
            <div className="col-12 col-md-5">
              <label className="form-label">Başlık</label>
              <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="form-control" required />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label">Sıra</label>
              <input type="number" value={newOrder} onChange={(e) => setNewOrder(Number(e.target.value))} className="form-control" />
            </div>
            <div className="col-6 col-md-1">
              <div className="form-check mt-4">
                <input id="newActive" className="form-check-input" type="checkbox" checked={newActive} onChange={(e) => setNewActive(e.target.checked)} />
                <label className="form-check-label" htmlFor="newActive">Aktif</label>
              </div>
            </div>
            <div className="col-12 col-md-1 text-end">
              <button type="submit" className="btn btn-success w-100" disabled={creating}>{creating ? 'Ekleniyor...' : 'Ekle'}</button>
            </div>
          </form>
          {error && <div className="mt-2 text-danger">{error}</div>}
        </div>
      </div>

      {/* List table */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle mb-0">
            <thead className="table-light sticky-top">
              <tr>
                <th>ID</th>
                <th>Başlık</th>
                <th>Sıra</th>
                <th>Aktif</th>
                <th>Güncellenme</th>
                <th className="text-end">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-muted p-3">Yükleniyor...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="text-muted p-3">Kayıt bulunamadı.</td></tr>
              )}
              {items.map((c) => (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td>
                    {editingId === c.id ? (
                      <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="form-control" />
                    ) : (
                      c.title
                    )}
                  </td>
                  <td>
                    {editingId === c.id ? (
                      <input type="number" value={editOrder} onChange={(e) => setEditOrder(Number(e.target.value))} className="form-control" style={{ maxWidth: 120 }} />
                    ) : (
                      c.orderIndex
                    )}
                  </td>
                  <td>
                    {editingId === c.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input id={`editActive-${c.id}`} type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />
                        <label htmlFor={`editActive-${c.id}`}>Aktif</label>
                      </div>
                    ) : (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 12,
                          background: c.isActive ? '#ecfdf5' : '#fef2f2',
                          color: c.isActive ? '#065f46' : '#991b1b',
                          border: `1px solid ${c.isActive ? '#a7f3d0' : '#fecaca'}`,
                        }}
                      >
                        {c.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    )}
                  </td>
                  <td>{c.updatedAt ? new Date(c.updatedAt).toLocaleString() : '-'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {editingId === c.id ? (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: '1px solid #d1d5db',
                            background: '#fff',
                            color: '#374151',
                            cursor: 'pointer',
                            opacity: saving ? 0.7 : 1,
                          }}
                        >
                          İptal
                        </button>
                        <button
                          onClick={() => onSave(c.id)}
                          disabled={saving}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: '1px solid #16a34a',
                            background: '#16a34a',
                            color: '#fff',
                            cursor: 'pointer',
                            opacity: saving ? 0.8 : 1,
                          }}
                        >
                          {saving ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => openEdit(c)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: '1px solid #2563eb',
                            background: '#fff',
                            color: '#2563eb',
                            cursor: 'pointer',
                          }}
                        >
                          Düzenle
                        </button>
                        <button
                          onClick={() => onDelete(c.id)}
                          disabled={deletingId === c.id}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: '1px solid #dc2626',
                            background: '#dc2626',
                            color: '#fff',
                            cursor: 'pointer',
                            opacity: deletingId === c.id ? 0.8 : 1,
                          }}
                        >
                          Sil
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {confirmId && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="deleteModalTitle"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmId(''); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setConfirmId(''); }}
          tabIndex={-1}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, 92vw)',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="deleteModalTitle" style={{ margin: 0, fontSize: 18 }}>Silme Onayı</h5>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setConfirmId('')}
                style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 16 }}>
              Bu kategoriyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setConfirmId('')}
                disabled={confirmLoading}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  cursor: 'pointer',
                  opacity: confirmLoading ? 0.7 : 1,
                }}
              >
                İptal
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={confirmLoading}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #dc2626',
                  background: '#dc2626',
                  color: '#fff',
                  cursor: 'pointer',
                  opacity: confirmLoading ? 0.8 : 1,
                }}
              >
                {confirmLoading ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnlasmaliCategoriesYonetim;
